import React, { useState, useEffect, useRef } from "react";

import Layout from "./Layout";
import useAuthentication from "../../hooks/useAuthentication";
import useInterval from "../../hooks/useInterval";

const INACTIVITY_TIME = 29 * 60 * 1000; // 29 minutes
const INACTIVITY_TIME_REMAINING = 300; // 5 minutes warning time

const STATUS = {
	STARTED: "Started",
	STOPPED: "Stopped",
};

const InactivityModal: React.FC = () => {
	const [secondsRemaining, setSecondsRemaining] = React.useState(INACTIVITY_TIME_REMAINING);
	const [showInactivityModal, setShowInactivityModal] = React.useState(false);
	const [status, setStatus] = useState(STATUS.STOPPED);
	const secondsToDisplay = secondsRemaining % 60;
	const minutesToDisplay = Math.floor(secondsRemaining / 60);

	const { handleLogout } = useAuthentication();
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useInterval({
		callback: () => {
			if (secondsRemaining > 0) {
				setSecondsRemaining(secondsRemaining - 1);
			} else {
				setStatus(STATUS.STOPPED);
				setShowInactivityModal(false);
				handleLogout();
			}
		},
		delay: status === STATUS.STARTED ? 1000 : null,
	});

	useEffect(() => {
		function displayInactivityModal() {
			setSecondsRemaining(INACTIVITY_TIME_REMAINING);
			setStatus(STATUS.STARTED);
			setShowInactivityModal(true);
		}

		function resetTimer() {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// Only reset timer if modal is not showing
			if (!showInactivityModal) {
				timeoutRef.current = setTimeout(displayInactivityModal, INACTIVITY_TIME);
			}
		}

		// Initial timer setup
		resetTimer();

		// DOM Events
		if (!showInactivityModal) {
			document.ontouchmove = resetTimer;
			document.onmousemove = resetTimer;
			document.onkeydown = resetTimer;
		}

		// Cleanup function
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			// Clean up event listeners
			document.ontouchmove = null;
			document.onmousemove = null;
			document.onkeydown = null;
		};
	}, [showInactivityModal]);

	// to handle modal closing
	useEffect(() => {
		if (!showInactivityModal) {
			setStatus(STATUS.STOPPED);
			setSecondsRemaining(INACTIVITY_TIME_REMAINING);
		}
	}, [showInactivityModal]);

	return (
		<Layout
			secondsToDisplay={secondsToDisplay}
			minutesToDisplay={minutesToDisplay}
			showInactivityModal={showInactivityModal}
			setShowInactivityModal={setShowInactivityModal}
		/>
	);
};

export default InactivityModal;
